import { useCallback, useEffect, useState } from "react";
import { getRatingsClient, ThrottleError, type RatingSummary } from "@/lib/ratings-client";

interface Props {
  recipeId: string;
}

export default function RatingWidget({ recipeId }: Props) {
  const [summary, setSummary] = useState<RatingSummary | null>(null);
  const [pending, setPending] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const client = getRatingsClient();

  useEffect(() => {
    let cancelled = false;
    client
      .getRating(recipeId)
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch(() => {
        // Ratings endpoint is optional in dev; silently treat as empty.
        if (!cancelled) setSummary({ count: 0, avg: 0 });
      });
    return () => {
      cancelled = true;
    };
  }, [client, recipeId]);

  const submit = useCallback(
    async (stars: number) => {
      if (pending) return;
      setPending(true);
      setToast(null);
      const optimistic: RatingSummary = summary
        ? {
            count: summary.count + 1,
            avg:
              Math.round(((summary.avg * summary.count + stars) / (summary.count + 1)) * 100) /
              100,
          }
        : { count: 1, avg: stars };
      setSummary(optimistic);
      try {
        const fresh = await client.submitRating(recipeId, stars);
        setSummary(fresh);
        setToast("Thanks for rating!");
      } catch (e) {
        // Roll back the optimistic update.
        setSummary(summary);
        if (e instanceof ThrottleError) {
          setToast("You've already rated this recipe today.");
        } else {
          setToast("Couldn't save your rating right now.");
        }
      } finally {
        setPending(false);
        window.setTimeout(() => setToast(null), 2500);
      }
    },
    [client, pending, recipeId, summary],
  );

  return (
    <div>
      <div
        role="radiogroup"
        aria-label="Rate this recipe"
        className="inline-flex items-center gap-1"
      >
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={summary ? Math.round(summary.avg) === n : false}
            aria-label={`${n} star${n === 1 ? "" : "s"}`}
            onClick={() => submit(n)}
            className={`w-7 h-7 rounded text-amber-500 hover:scale-110 transition-transform ${
              summary && summary.avg >= n ? "opacity-100" : "opacity-40"
            }`}
          >
            ★
          </button>
        ))}
        <span className="ml-2 text-sm text-slate-600 dark:text-slate-400">
          {summary && summary.count > 0
            ? `${summary.avg.toFixed(1)} (${summary.count})`
            : "No ratings yet"}
        </span>
      </div>
      {toast ? (
        <p className="mt-1 text-xs text-slate-600 dark:text-slate-400" role="status">
          {toast}
        </p>
      ) : null}
    </div>
  );
}
